import { useTranslation } from 'react-i18next';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminEditShipmentAddress, type ShipmentAddressEditPayload } from '../../api/endpoints/admin';
import type { PlaceDetail } from '../../api/endpoints/places';
import type { AddressResponse, ShipmentResponse } from '../../api/types';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { AddressAutocompleteField } from '../../components/domain/AddressAutocompleteField';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { formatDistance } from '../../utils/distance';
import { ApproxMarker } from './ApproxMarker';
import formStyles from '../../components/domain/FormModal.module.css';
import styles from './EditStopAddressModal.module.css';
import { DialogTitleRow } from '../../help/DialogTitleRow';

type Stop = ShipmentAddressEditPayload['stop'];

interface Props {
  shipment: ShipmentResponse;
  stop: Stop;
  onClose: () => void;
}

const REASON_MIN = 3;
const REASON_MAX = 300;

function addressText(structured: AddressResponse | null, flat: string, city: string): string {
  if (structured) return [structured.line, structured.city].filter(Boolean).join(', ');
  return [flat, city].filter(Boolean).join(', ');
}

function stopAddress(shipment: ShipmentResponse, stop: Stop): string {
  return stop === 'PICKUP'
    ? addressText(shipment.pickupAddress, shipment.originAddress, shipment.originCity)
    : addressText(shipment.dropAddress, shipment.destinationAddress, shipment.destinationCity);
}

function buildBody(stop: Stop, place: PlaceDetail, reason: string): ShipmentAddressEditPayload {
  const optional = (value: string | undefined) => (value?.trim() ? value.trim() : undefined);
  const district = optional(place.district);
  const state = optional(place.state);
  const pincode = optional(place.pincode);
  const placeId = optional(place.placeId);
  return {
    stop,
    address: place.formattedAddress.trim().slice(0, 500),
    city: place.city.trim(),
    ...(district ? { district } : {}),
    ...(state ? { state } : {}),
    ...(pincode ? { pincode } : {}),
    latitude: place.lat,
    longitude: place.lng,
    ...(placeId ? { placeId } : {}),
    reason,
  };
}

/**
 * Changes the pickup or drop address after a driver is assigned. The price is not changed here;
 * the result links to the order's Payments tab instead.
 */
export function EditStopAddressModal({ shipment, stop, onClose }: Props) {
  const queryClient = useQueryClient();
  const word = stop === 'PICKUP' ? 'pickup' : 'drop';
  const title = stop === 'PICKUP' ? 'Edit pickup' : 'Edit drop';
  const label = stop === 'PICKUP' ? 'Pickup address' : 'Drop address';

  const { t } = useTranslation();
  const [text, setText] = useState(() => stopAddress(shipment, stop));
  const [place, setPlace] = useState<PlaceDetail | null>(null);
  const [reason, setReason] = useState('');
  // Distance before the change, kept while the page refetches the shipment underneath.
  const [before] = useState(() => ({
    km: shipment.distanceKm,
    estimated: shipment.distanceIsEstimated,
  }));
  const lastBody = useRef<ShipmentAddressEditPayload | null>(null);
  const sending = useRef(false);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const placeValid = place != null && place.city.trim() !== '';

  const save = useMutation<ShipmentResponse, Error, ShipmentAddressEditPayload>({
    mutationFn: (body) => adminEditShipmentAddress(shipment.id, body),
    onSuccess: () => {
      for (const key of [
        ['admin', 'shipments', 'detail', shipment.id],
        ['admin', 'shipments', 'tracking', shipment.id],
        ['admin', 'shipments'],
      ]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (error) => {
      if (error instanceof DnmRequestFailure && error.code === 'ADDRESS_EDIT_NOT_ALLOWED') {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'shipments', 'detail', shipment.id] });
      }
    },
    onSettled: () => {
      sending.current = false;
    },
  });

  const notAllowed =
    save.error instanceof DnmRequestFailure && save.error.code === 'ADDRESS_EDIT_NOT_ALLOWED';

  const send = (body: ShipmentAddressEditPayload) => {
    if (sending.current) return;
    sending.current = true;
    lastBody.current = body;
    save.mutate(body);
  };

  // A changed form makes the next send a fresh Save, not a Retry of the old body.
  const edited = () => {
    if (save.error && !notAllowed) save.reset();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!placeValid || !reasonValid || save.isPending) return;
    send(buildBody(stop, place, trimmedReason));
  };

  const errorText = !save.error
    ? null
    : notAllowed
      ? `The trip has moved on, so the ${word} can’t be changed now. Close and check the current status.`
      : save.error instanceof DnmRequestFailure && save.error.response
        ? save.error.message
        : 'Couldn’t save the address. Check your connection and try again.';

  const result = save.data;
  const busy = save.isPending;
  const dirty = useIsDirty({ text, place, reason });
  // Once saved there is nothing left to lose, so Escape closes the result view at once.
  const dismiss = useDialogDismiss({ dirty: dirty && !result, busy, onClose });

  return (
    <DialogShell
      dismiss={dismiss}
      label={t('shipments.editStopAddressModal.heading', { title, shipmentId: shipment.id })}
      scrimClassName={formStyles.scrim}
      panelClassName={formStyles.panel}
    >
      <DialogTitleRow context="trip-problems" title={t('shipments.editStopAddressModal.heading', { title, shipmentId: shipment.id })}>
        <h3 className={`${formStyles.title} ${styles.title}`}>
          {t('shipments.editStopAddressModal.heading', { title, shipmentId: shipment.id })}
        </h3>
      </DialogTitleRow>

      {result ? (
        <div className={styles.result}>
          <p className={styles.resultHead}>
            <span aria-hidden="true" className={styles.doneIcon}>
              ✓
            </span>
            {t('shipments.editStopAddressModal.addressUpdated')}
          </p>
          <div className={`${formStyles.hint} ${styles.resultLabel}`}>{label}</div>
          <div className={styles.resultValue}>{stopAddress(result, stop)}</div>
          <div className={`${formStyles.hint} ${styles.resultLabel}`}>{t('shipments.editStopAddressModal.distance')}</div>
          <div className={styles.resultValue} data-testid="distance-change">
            {formatDistance(before.km) ?? '—'}
            <ApproxMarker estimated={before.estimated} /> → {formatDistance(result.distanceKm) ?? '—'}
            <ApproxMarker estimated={result.distanceIsEstimated} />
          </div>
          <p className={styles.priceNote}>
            {t('shipments.editStopAddressModal.priceUnchanged')}{' '}
            <Link to={`/shipments/${shipment.id}?tab=payments`} className={styles.link} onClick={onClose}>
              {t('shipments.editStopAddressModal.changePrice')}
            </Link>
          </p>
          <div className={formStyles.actions}>
            <button type="button" className={formStyles.submit} onClick={onClose}>
              {t('shipments.editStopAddressModal.done')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <p className={formStyles.hint}>
            {t('shipments.editStopAddressModal.driverAssigned')}
          </p>
          <div className={styles.field}>
            <AddressAutocompleteField
              label={label}
              placeholder={t('shipments.editStopAddressModal.addressPlaceholder')}
              value={text}
              onChange={(value) => {
                edited();
                setText(value);
              }}
              onPlaceSelected={setPlace}
              onCleared={() => setPlace(null)}
              pinTone={stop === 'PICKUP' ? 'origin' : 'destination'}
              required
            />
            {place && !placeValid && (
              <div className={formStyles.hint}>{t('shipments.editStopAddressModal.placeInvalid')}</div>
            )}
          </div>

          <label className={formStyles.label} htmlFor="edit-stop-reason">
            {t('shipments.editStopAddressModal.reasonLabel')}
          </label>
          <textarea
            id="edit-stop-reason"
            className={`${formStyles.input} ${styles.reason}`}
            rows={3}
            placeholder={t('shipments.editStopAddressModal.reasonPlaceholder')}
            value={reason}
            onChange={(e) => {
              edited();
              setReason(e.target.value);
            }}
            disabled={busy}
          />
          <div className={`${formStyles.hint} ${styles.reasonMeta}`}>
            <span className={styles.reasonError}>
              {reason !== '' && !reasonValid ? 'Write a reason of 3 to 300 characters.' : ''}
            </span>
            <span>
              {trimmedReason.length}/{REASON_MAX}
            </span>
          </div>

          {errorText && (
            <div className={formStyles.error} role="alert">
              {errorText}
            </div>
          )}

          <div className={formStyles.actions}>
            {notAllowed ? (
              <button type="button" className={formStyles.submit} onClick={onClose}>
                {t('common.actions.close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className={formStyles.cancel}
                  onClick={dismiss.requestClose}
                  disabled={busy}
                >
                  {t('common.actions.cancel')}
                </button>
                {save.error && lastBody.current ? (
                  <button
                    type="button"
                    className={formStyles.submit}
                    disabled={busy}
                    onClick={() => lastBody.current && send(lastBody.current)}
                  >
                    {t('shipments.editStopAddressModal.retry')}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className={formStyles.submit}
                    disabled={!placeValid || !reasonValid || busy}
                  >
                    {busy ? 'Saving…' : `Save ${word}`}
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      )}
    </DialogShell>
  );
}
